<template>
  <div class="layout__container">
    <h3 class="text__align-center text__colour-teal">Abandoned Orders Report</h3>
    <div class="layout__position-center">
      <div class="layout__position-left layout__width-100">
        <AbandonedOrdersSearch
          v-if="! isGettingTotal"
          :sales="sales"
          :search-params="searchParams"
          @update-search-params="onUpdateSearchParams"
        />
      </div>
      <div class="insights-table layout__width-100 layout__position-left">
        <el-table
          class="el-table__09vh"
          :data="sales"
          height="620px"
          @sort-change="handleSortOrderChange"
          v-loading.fullscreen.lock="loading || isGettingTotal"
          element-loading-text="Loading..."
          element-loading-background="rgba(79, 147, 168, 0.2)"
          empty-text="No data has been returned for the selected dates"
          @cell-click="onCellClick"
        >
          <el-table-column
            v-for="abandonedOrder in AbandonedOrders.fields()"
            :key="`${abandonedOrder}.${new Date()}`"
            :prop="abandonedOrder"
            :show-overflow-tooltip="true"
            :min-width="AbandonedOrders.getColumnWidth(abandonedOrder)"
            :label="AbandonedOrders.getReadable(abandonedOrder)"
            class-name="layout__width-150px el-table-column__no-pointer"
            sortable="custom"
          />
        </el-table>
      </div>

      <div
        v-if="searchParams.total"
        class="layout__padding-clear layout__width-60"
      >
        <div class="layout__padding-20-0 text__align-center">
          <el-pagination
            :current-page="searchParams.currentPage"
            :page-size="searchParams.pagination"
            layout="prev, pager, next"
            :total="searchParams.total"
            @update:currentPage="onCurrentPageUpdate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Axios from 'axios';
import AbandonedOrdersSearch from '@/components/insights/AbandonedOrdersSearch';
import { axios } from '@/mixins/Axios.js';
import { csvDownloadHelper } from '@/mixins/CsvDownloadHelper.js';
import { dataFormatter } from '@/mixins/DataFormatter.js';
import { sortJsonArrayHelper } from '@/mixins/sort-json-array.js';
import AbandonedOrders from '../../../enums/AbandonedOrders';
import moment from 'moment';

export default {
  name: 'AbandonedOrdersReport',

  mixins: [
    axios,
    csvDownloadHelper,
    dataFormatter,
    sortJsonArrayHelper,
  ],

  components: {
    AbandonedOrdersSearch,
  },

  props: {
    view: '',
  },

  data() {
    return {
      sales: [],
      loading: false,
      isGettingTotal: false,
      loadings: {},
      AbandonedOrders: AbandonedOrders,
      serviceRouteUrl: `${process.env.VUE_APP_INSIGHTSAPIHOST}`,
      exportDate: new Date().toLocaleDateString('en-GB') + '_abandoned_orders.csv',
      searchParams: {
        groupBy: 'MONTH',
        currentPage: 1,
        pagination: 40,
        total: 0,
        fromDate: moment().startOf('month').format('yyyy-MM-DD'),
        branch: '',
        daysRange: this.defaultMonthRange(),
      },
    };
  },

  computed: {
    isMonthReport() {
      return this.searchParams.groupBy === 'MONTH';
    },

    isDayReport() {
      return this.searchParams.groupBy === 'DAY';
    },
  },

  async created() {
    this.isUserAuthorised();

    await this.getTotals();
  },

  methods: {
    defaultMonthRange() {
      return [
        moment().startOf('month').format('yyyy-MM-DD'),
        moment().endOf('month').format('yyyy-MM-DD'),
      ];
    },

    prepareParams(getCount = 0) {
      const params = {};

      params.groupBy = this.searchParams.groupBy;

      if (getCount === 1) {
        params.getCount = '1';
        params.start = '0';
        params.cnt = '0';
      } else {
        params.getCount = '0';
        params.cnt = this.searchParams.pagination;
        params.start = this.searchParams.pagination * (this.searchParams.currentPage - 1);
      }

      if (this.isMonthReport) {
        params.fromDate = this.searchParams.fromDate;
        params.toDate = moment(params.fromDate).endOf('month').format('yyyy-MM-DD');
      }

      if (this.isDayReport) {
        params.fromDate = this.searchParams.daysRange[0];
        params.toDate = this.searchParams.daysRange[1];
      }

      if (this.searchParams.branch) {
        params.branch = this.searchParams.branch;
      }

      return new URLSearchParams(params).toString();
    },

    async getTotals() {
      this.isGettingTotal = true;

      this.isUserAuthorised();

      const url = `${this.serviceRouteUrl}/api/v1/DynamicQuery/GetAbandonedOrders?${this.prepareParams(1)}`;

      this.searchParams.total = (await Axios.get(url, this.returnHeaderUsingToken())).data[0]?.count || 0;

      this.isGettingTotal = false;
    },

    handleSortOrderChange(sortProps) {
      this.sortJsonArray(this.sales, sortProps.prop, sortProps.order.slice(0, 3));
    },

    csv(index) {
      this.$set(this.loadings, index, false);

      const endpoint = `${this.serviceRouteUrl}/api/v1/DynamicQuery/GetAbandonedOrders?reportType=${this.searchParams.reportType}`;

      Axios.get(endpoint, this.returnHeaderUsingToken())
        .then(response => {
          const fields = this.AbandonedOrders.createLabelValueList();

          this.downloadCsv(this.exportDate, fields, response.data);

          this.$nextTick(() => { // Loading should be closed asynchronously
            this.$set(this.loadings, index, false);
          });
        })
        .catch(error => {
          if (error.response) {
            this.$message.error('Sorry there has been an issue retrieving the information you requested.');
          }

          this.$nextTick(() => { // Loading should be closed asynchronously
            this.$set(this.loadings, index, false);
          });
        });
    },

    onCurrentPageUpdate(page) {
      this.searchParams.currentPage = page;
    },

    async onUpdateSearchParams() {
      this.searchParams.currentPage = 1;
      this.searchParams.total = 0;

      await this.getTotals();
    },

    onCellClick(row, col) {
      if (col.property !== 'BranchId') {
        return;
      }

      this.searchParams.branch = row.BranchId;
    },
  },
};
</script>